import React from 'react';
import { motion } from 'framer-motion';
import SEOHead from '../components/seo/SEOHead';
import useScrollToTop from '../hooks/useScrollToTop';
import Header from '../components/core/Header';
import Footer from '../components/core/Footer';

const Terms = () => {
  useScrollToTop();

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead 
        title="Terms of Service - CustomSync"
        description="CustomSync Terms of Service - Learn about the terms and conditions for using our service."
        keywords="terms of service, terms and conditions, CustomSync terms"
        type="article"
        url="https://customsync.app/terms"
      />
      
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-4">CustomSync Terms of Service</h1>
          <p className="text-gray-600 mb-8">Effective Date: October 7, 2025</p>
          
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 mb-6">
              Welcome to CustomSync ("we," "our," or "us"). By using the CustomSync web application, website, 
              or related services ("Services"), you agree to these Terms of Service ("Terms"). Please read them carefully.
            </p>
            
            <p className="text-gray-700 mb-8 font-semibold">
              If you do not agree to these Terms, you may not use the Services.
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Overview</h2>
              <p className="text-gray-700 mb-3">
                CustomSync is a software integration tool that connects Amazon Seller accounts with third-party platforms 
                such as ShipStation and Veeqo. It automates the import of Amazon Custom order data, including customization 
                details (text and images), into those systems for efficient order fulfillment.
              </p>
              <p className="text-gray-700">
                You must have a valid Amazon Seller account to use CustomSync.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Eligibility</h2>
              <p className="text-gray-700 mb-3">To use CustomSync, you must:</p>
              <ul className="list-disc list-inside text-gray-700 ml-4 space-y-2">
                <li>Be at least 18 years old,</li>
                <li>Have the authority to agree to these Terms on behalf of your business, and</li>
                <li>Comply with all applicable laws and Amazon Selling Partner API (SP-API) policies.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Account Registration</h2>
              <p className="text-gray-700 mb-3">
                You must create an account to access the Services. When registering, you agree to:
              </p>
              <ul className="list-disc list-inside text-gray-700 ml-4 space-y-2">
                <li>Provide accurate and complete information,</li>
                <li>Maintain the confidentiality of your login credentials, and</li>
                <li>Notify us immediately if you suspect unauthorized use of your account.</li>
              </ul>
              <p className="text-gray-700 mt-4">
                We are not responsible for losses caused by unauthorized access to your account.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Use of Services</h2>
              <p className="text-gray-700 mb-3">
                You agree to use CustomSync solely for lawful purposes and in accordance with these Terms and 
                Amazon's Data Protection Policy.
              </p>
              <p className="text-gray-700 mb-3">You may not:</p>
              <ul className="list-disc list-inside text-gray-700 ml-4 space-y-2">
                <li>Use the Services to transmit or store illegal, infringing, or harmful content;</li>
                <li>Reverse engineer, copy, or resell CustomSync;</li>
                <li>Share API credentials or access tokens with unauthorized users;</li>
                <li>Circumvent rate limits or abuse the platform.</li>
              </ul>
              <p className="text-gray-700 mt-4">
                Violation of these terms may result in suspension or termination of your account.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Data Access & Permissions</h2>
              <p className="text-gray-700 mb-3">
                By connecting your Amazon account, you authorize CustomSync to access certain order data through 
                the Amazon Selling Partner API (SP-API). This data is used only to perform synchronization functions 
                you initiate — such as sending Amazon Custom order details to ShipStation or Veeqo.
              </p>
              <p className="text-gray-700 mb-4">
                We do not share, sell, or disclose any Amazon data to third parties other than the integrations you 
                explicitly configure.
              </p>
              <p className="text-gray-700">
                For more information on how we handle your data, see our{' '}
                <a href="/privacy" className="text-blue-600 hover:text-blue-800 underline">Privacy Policy</a>.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Payment and Subscription</h2>
              <p className="text-gray-700 mb-3">
                Certain features of CustomSync may require a paid subscription. Payment terms, pricing, and renewal 
                conditions will be displayed at checkout or within your account settings.
              </p>
              <p className="text-gray-700 mb-3">Unless otherwise stated:</p>
              <ul className="list-disc list-inside text-gray-700 ml-4 space-y-2">
                <li>Subscriptions auto-renew monthly or annually.</li>
                <li>You may cancel at any time before the next billing cycle.</li>
                <li>We do not offer refunds for partial billing periods.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Data Security and Privacy</h2>
              <p className="text-gray-700 mb-3">
                We take data security seriously. All Amazon order data is transmitted using TLS 1.2+, encrypted at 
                rest using AES-256, and automatically deleted after processing or within 30 days.
              </p>
              <p className="text-gray-700">
                For details, please review our{' '}
                <a href="/privacy" className="text-blue-600 hover:text-blue-800 underline">
                  Security, Privacy & Data Handling Policy
                </a>.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Service Availability</h2>
              <p className="text-gray-700 mb-3">
                We strive to provide continuous, reliable service but do not guarantee 100% uptime. We may occasionally 
                suspend or modify the Services for maintenance or updates.
              </p>
              <p className="text-gray-700">
                CustomSync is provided "as is," without warranties of any kind, including but not limited to merchantability, 
                fitness for a particular purpose, or non-infringement.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Limitation of Liability</h2>
              <p className="text-gray-700 mb-3">
                To the maximum extent permitted by law, CustomSync and its developers shall not be liable for:
              </p>
              <ul className="list-disc list-inside text-gray-700 ml-4 space-y-2">
                <li>Any indirect, incidental, or consequential damages;</li>
                <li>Any loss of profits, revenue, or data;</li>
                <li>Any issues arising from third-party services (e.g., Amazon, ShipStation, Veeqo).</li>
              </ul>
              <p className="text-gray-700 mt-4">
                Your sole remedy for dissatisfaction with the Services is to discontinue use.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Termination</h2>
              <p className="text-gray-700 mb-3">
                You may stop using CustomSync at any time. We reserve the right to suspend or terminate your account 
                if you violate these Terms or misuse the Services.
              </p>
              <p className="text-gray-700">
                Upon termination, we will delete all stored data within 30 days unless otherwise required by law.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Changes to the Terms</h2>
              <p className="text-gray-700">
                We may update these Terms periodically. Any changes will be effective upon posting to{' '}
                <a href="https://customsync.app/terms" className="text-blue-600 hover:text-blue-800 underline">
                  https://customsync.app/terms
                </a>.
                Your continued use of the Services after such updates constitutes your acceptance.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Governing Law</h2>
              <p className="text-gray-700">
                These Terms are governed by and construed in accordance with the laws of the State of Michigan, USA, 
                without regard to conflict of law principles.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Contact Us</h2>
              <p className="text-gray-700 mb-3">
                If you have any questions about these Terms or the Services, please contact:
              </p>
              <div className="bg-gray-50 rounded-lg">
                <p className="text-gray-900 font-semibold mb-2">CustomSync</p>
                <p className="text-gray-700">
                  Email:{' '}
                  <a href="mailto:hello@customsync.app" className="text-blue-600 hover:text-blue-800 underline">
                    hello@customsync.app
                  </a>
                <br />
                  Website:{' '}
                  <a href="https://customsync.app" className="text-blue-600 hover:text-blue-800 underline" target="_blank" rel="noopener noreferrer">
                    https://customsync.app
                  </a>
                </p>
              </div>
            </section>
          </div>
        </motion.div>
      </div>
      
      <Footer />
    </div>
  );
};

export default Terms;

